import { FC, useState } from 'react';
import { Button, Card, Divider, Input, Label, RadixTable } from '@features/ui';
import { toast } from 'react-toastify';
import { TReportUserLifetimeData } from '@src/types/reports';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { getUserLifetime, exportUserLifetime } from '@src/services/reports';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: TReportUserLifetimeData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[160px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {data?.number_of_users?.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px]">{data?.lifetime_range}</p>
        <p className="text-white text-xs">Number of Users</p>
      </div>
    );
  }

  return null;
};

export const UserLifetime: FC = () => {
  const [groupPeriodByDays, setGroupPeriodByDays] = useState<string>('30');
  const [data, setData] = useState<TReportUserLifetimeData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { columns, tableData } = useReportTableColumns<TReportUserLifetimeData>(
    {
      data,
      getAccessField: (item) => item.lifetime_range,
      getMainDataField: (item) => item.number_of_users || 0,
      customColumns: [
        {
          title: 'NUMBER OF USERS',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              {(item?.number_of_users ?? 0).toLocaleString()}
            </p>
          ),
        },
      ],
      includeTotalsRow: true,
    }
  );

  const handleRunReport = async () => {
    try {
      setIsLoading(true);
      const res = await getUserLifetime({
        group_period_days: Number(groupPeriodByDays),
      });

      setData(res.data);
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const res = await exportUserLifetime({
        group_period_days: Number(groupPeriodByDays),
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `user_lifetime.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error exporting report');
      console.error('Error exporting report:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">User Lifetime</h1>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="group-period-by-days">
                Group Period by number of days
              </Label>
              <Input
                id="group-period-by-days"
                min="1"
                placeholder="Enter number of days"
                type="number"
                value={groupPeriodByDays}
                onChange={(e) => setGroupPeriodByDays(e.target.value)}
              />
            </div>
          </div>

          <div className="flex mt-6">
            <Button
              className="bg-[#B6D72C] text-black hover:bg-[#a0c127]"
              disabled={isLoading || !groupPeriodByDays}
              onClick={handleRunReport}
            >
              {isLoading ? 'Loading...' : 'Run Report'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="lifetime_range"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                dataKey="number_of_users"
                fill="#B6D72C"
                name="Number of Users"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              disabled={isExporting || data.length === 0}
              variant="outline"
              onClick={handleExport}
            >
              {isExporting ? 'Exporting...' : 'Download CSV'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
